/**
 * RepoFileExplorer — browsable file trees for all GitHub repos attached to a chat.
 *
 * Shows every attached repo as a collapsible section, lets the user multi-select
 * files across repos, preview any file, and drop qualified references into chat.
 */

import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  File as FileIcon,
  Folder,
  FolderOpen,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import * as githubService from '@/services/githubService';

const MAX_FILE_SELECTION = 10;

type RepoRef = { owner: string; name: string; fullName: string };

interface TreeNode {
  name: string;
  path: string;
  type: 'dir' | 'file';
  children: TreeNode[];
}

function selectionKey(fullName: string, path: string): string {
  return `${fullName}::${path}`;
}

function parseSelectionKey(key: string): { fullName: string; path: string } | null {
  const sep = key.indexOf('::');
  if (sep <= 0) return null;
  return { fullName: key.slice(0, sep), path: key.slice(sep + 2) };
}

function buildTree(paths: Array<{ path: string; type: string }>): TreeNode[] {
  const root: TreeNode = { name: '', path: '', type: 'dir', children: [] };
  const dirIndex = new Map<string, TreeNode>([['', root]]);

  const sorted = [...paths].sort((a, b) => a.path.localeCompare(b.path));
  for (const item of sorted) {
    const segments = item.path.split('/');
    let parent = root;
    let currentPath = '';
    for (let i = 0; i < segments.length; i++) {
      const isLeaf = i === segments.length - 1;
      currentPath = currentPath ? `${currentPath}/${segments[i]}` : segments[i];
      if (isLeaf && item.type === 'blob') {
        parent.children.push({ name: segments[i], path: currentPath, type: 'file', children: [] });
        continue;
      }
      let dir = dirIndex.get(currentPath);
      if (!dir) {
        dir = { name: segments[i], path: currentPath, type: 'dir', children: [] };
        dirIndex.set(currentPath, dir);
        parent.children.push(dir);
      }
      parent = dir;
    }
  }

  const sortChildren = (node: TreeNode) => {
    node.children.sort((a, b) =>
      a.type !== b.type ? (a.type === 'dir' ? -1 : 1) : a.name.localeCompare(b.name)
    );
    node.children.forEach(sortChildren);
  };
  sortChildren(root);
  return root.children;
}

function TreeRow({
  node,
  depth,
  repoFullName,
  expanded,
  selected,
  onToggle,
  onFileClick,
  onToggleSelect,
}: {
  node: TreeNode;
  depth: number;
  repoFullName: string;
  expanded: Set<string>;
  selected: Set<string>;
  onToggle: (path: string) => void;
  onFileClick: (path: string) => void;
  onToggleSelect: (path: string, checked: boolean) => void;
}) {
  const isOpen = expanded.has(node.path);
  const key = selectionKey(repoFullName, node.path);
  const isChecked = selected.has(key);

  return (
    <div>
      <div
        className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-xs hover:bg-muted"
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
      >
        {node.type === 'file' ? (
          <Checkbox
            checked={isChecked}
            onCheckedChange={(v) => onToggleSelect(node.path, v === true)}
            onClick={(e) => e.stopPropagation()}
            className="h-3.5 w-3.5 shrink-0"
            aria-label={`Select ${node.path}`}
            data-testid={`repo-explorer-check-${repoFullName}-${node.path}`}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          onClick={() => (node.type === 'dir' ? onToggle(node.path) : onFileClick(node.path))}
          data-testid={`repo-explorer-node-${repoFullName}-${node.path}`}
        >
          {node.type === 'dir' ? (
            <>
              {isOpen ? (
                <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
              )}
              {isOpen ? (
                <FolderOpen className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              ) : (
                <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              )}
            </>
          ) : (
            <FileIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{node.name}</span>
        </button>
      </div>
      {node.type === 'dir' && isOpen && (
        <div>
          {node.children.map((child) => (
            <TreeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              repoFullName={repoFullName}
              expanded={expanded}
              selected={selected}
              onToggle={onToggle}
              onFileClick={onFileClick}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RepoTreeSection({
  repo,
  defaultOpen,
  selected,
  onToggleSelect,
  onPreview,
}: {
  repo: RepoRef;
  defaultOpen: boolean;
  selected: Set<string>;
  onToggleSelect: (repo: RepoRef, path: string, checked: boolean) => void;
  onPreview: (repo: RepoRef, path: string) => void;
}) {
  const [sectionOpen, setSectionOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const {
    data: tree,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['repo-explorer-tree', repo.fullName],
    queryFn: () => githubService.getRepoTree(repo.owner, repo.name),
    staleTime: 5 * 60 * 1000,
  });

  const nodes = useMemo(() => (tree ? buildTree(tree.tree) : []), [tree]);

  const toggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-1 px-1 py-1">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-1.5 text-left text-xs font-medium hover:bg-muted"
          onClick={() => setSectionOpen((o) => !o)}
          data-testid={`repo-explorer-section-${repo.fullName}`}
        >
          {sectionOpen ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate" title={repo.fullName}>
            {repo.fullName}
          </span>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label={`Refresh ${repo.fullName}`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {sectionOpen && (
        <div className="pb-1">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading file tree…
            </div>
          ) : isError ? (
            <div className="px-4 py-2 text-xs text-destructive">
              Failed to load the file tree.{' '}
              <button type="button" className="underline" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : (
            nodes.map((node) => (
              <TreeRow
                key={node.path}
                node={node}
                depth={0}
                repoFullName={repo.fullName}
                expanded={expanded}
                selected={selected}
                onToggle={toggle}
                onFileClick={(path) => onPreview(repo, path)}
                onToggleSelect={(path, checked) => onToggleSelect(repo, path, checked)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export interface RepoFileExplorerProps {
  repos: RepoRef[];
  /** Insert one or more file references into the chat input. */
  onInsertReferences: (items: Array<{ path: string; repo: RepoRef }>) => void;
}

export default function RepoFileExplorer({ repos, onInsertReferences }: RepoFileExplorerProps) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ repo: RepoRef; path: string } | null>(null);

  // Prefetch all trees so accordion sections hydrate quickly when opened
  useQueries({
    queries: repos.map((repo) => ({
      queryKey: ['repo-explorer-tree', repo.fullName] as const,
      queryFn: () => githubService.getRepoTree(repo.owner, repo.name),
      staleTime: 5 * 60 * 1000,
      enabled: repos.length > 0,
    })),
  });

  const {
    data: previewFile,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['repo-explorer-file', preview?.repo.fullName, preview?.path],
    queryFn: () =>
      githubService.getFileContent(preview!.repo.owner, preview!.repo.name, preview!.path),
    enabled: !!preview,
    staleTime: 60 * 1000,
  });

  const handleToggleSelect = (repo: RepoRef, path: string, checked: boolean) => {
    const key = selectionKey(repo.fullName, path);
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        if (next.has(key)) return prev;
        if (next.size >= MAX_FILE_SELECTION) {
          toast({
            title: 'Selection limit',
            description: `You can reference up to ${MAX_FILE_SELECTION} files at once.`,
            variant: 'destructive',
          });
          return prev;
        }
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const selectedItems = useMemo(() => {
    const items: Array<{ path: string; repo: RepoRef }> = [];
    for (const key of selected) {
      const parsed = parseSelectionKey(key);
      if (!parsed) continue;
      const repo = repos.find((r) => r.fullName === parsed.fullName);
      if (repo) items.push({ path: parsed.path, repo });
    }
    return items;
  }, [selected, repos]);

  const insertSelected = () => {
    if (selectedItems.length === 0) return;
    onInsertReferences(selectedItems);
    setSelected(new Set());
  };

  if (repos.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Attach a GitHub repository to browse its files.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <ScrollArea className="min-h-0 flex-1 rounded border">
        <div className="py-0.5">
          {repos.map((repo, index) => (
            <RepoTreeSection
              key={repo.fullName}
              repo={repo}
              defaultOpen={index === 0}
              selected={selected}
              onToggleSelect={handleToggleSelect}
              onPreview={(r, path) => setPreview({ repo: r, path })}
            />
          ))}
        </div>
      </ScrollArea>

      {selected.size > 0 && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t pt-2">
          <span className="text-xs text-muted-foreground">
            {selected.size} file{selected.size === 1 ? '' : 's'} selected
          </span>
          <Button
            size="sm"
            onClick={insertSelected}
            data-testid="repo-explorer-reference-selected"
          >
            <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
            Reference {selected.size} in chat
          </Button>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col">
          <DialogHeader>
            <DialogTitle className="truncate text-sm" title={preview?.path ?? ''}>
              {preview?.path}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {preview?.repo.fullName}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1 rounded border bg-muted/30">
            {isPreviewLoading ? (
              <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading file…
              </div>
            ) : isPreviewError ? (
              <div className="p-4 text-xs text-destructive">Failed to load this file.</div>
            ) : (
              <pre className="whitespace-pre-wrap break-words p-3 text-xs">
                {previewFile?.content ?? ''}
              </pre>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button
              size="sm"
              onClick={() => {
                if (preview) {
                  onInsertReferences([{ path: preview.path, repo: preview.repo }]);
                  setPreview(null);
                }
              }}
              data-testid="repo-explorer-reference-button"
            >
              <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
              Reference in chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
